import addMinutes from 'date-fns/addMinutes';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import format from 'date-fns/format';
import React from 'react';

import { areIntervalsOverlapping } from 'date-fns';
import {
  deselectedProgramItem,
  loadCurrentTime,
  loadEPGDataFetchSuccess,
  loadSwiperSlideLength,
  loadTimeListInDay,
} from './actions';
import { informationOfTheDay } from './utils';

/**
 * Hook Effect to get EPG Api content data
 *
 * @export
 * @returns
 */
export function useContentEPGInfo(
  content,
  selectedDay,
  inputSearch,
  dispatch,
  useCategoriesFilterInEpg = false,
  selectedCategory = null,
) {
  const filterEpg = (
    items,
    selectedDay,
    inputSearch,
    useCategoriesFilterInEpg,
    selectedCategory,
  ) =>
    items
      .map((programList) => {
        const epgResult = programList.epg.filter((epgItem) => {
          if (
            !epgItem ||
            !epgItem?.startTime ||
            !epgItem?.endTime ||
            new Date(epgItem?.startTime) > new Date(epgItem?.endTime)
          ) {
            console.log(
              '[ERROR - EPG ---> epgItem is not correct or  endTime < startTime]: ',
              epgItem,
            );
            return false;
          }

          const { from, to } = informationOfTheDay(selectedDay);
          return areIntervalsOverlapping(
            { start: from, end: to },
            {
              start: new Date(epgItem.startTime),
              end: new Date(epgItem.endTime),
            },
            { inclusive: false },
          );
        });
        return {
          ...programList,
          epg: epgResult,
        };
      })
      .filter(
        (programListR) =>
          programListR.title.toUpperCase().search(inputSearch.toUpperCase()) !==
            -1 &&
          (!useCategoriesFilterInEpg ||
            (!selectedCategory && useCategoriesFilterInEpg) ||
            (useCategoriesFilterInEpg &&
              selectedCategory &&
              programListR?.category?.toUpperCase() ===
                selectedCategory?.toUpperCase())),
      );

  React.useEffect(() => {
    if (selectedDay && content) {
      dispatch(
        loadEPGDataFetchSuccess(
          filterEpg(
            content,
            selectedDay,
            inputSearch,
            useCategoriesFilterInEpg,
            selectedCategory,
          ),
        ),
      );
    }
  }, [content, selectedDay, inputSearch, selectedCategory]);
}

/**
 * Hook Effect to format EPG
 *
 * @export
 * @returns
 */
export function useContentEPGFormatTime(selectedDay, dispatch) {
  // const [arrayFromTime, setArrayFromTimee] = React.useState([])
  const { from, to } = informationOfTheDay(selectedDay);
  // console.log('100%: ',differenceInMilliseconds(to,from))

  React.useEffect(() => {
    const timeListInDay = [
      { position: 0, time: from, formattedTime: format(from, 'HH:mm') },
    ];
    while (
      differenceInMilliseconds(
        addMinutes(timeListInDay[timeListInDay.length - 1].time, 30),
        to,
      ) < 0
    ) {
      const newTime = addMinutes(
        timeListInDay[timeListInDay.length - 1].time,
        30,
      );
      timeListInDay.push({
        position: timeListInDay[timeListInDay.length - 1].position + 1,
        time: newTime,
        formattedTime: format(newTime, 'HH:mm'),
      });
    }
    // setArrayFromTimee(fromTime)
    dispatch(loadTimeListInDay(timeListInDay));
    dispatch(loadSwiperSlideLength(timeListInDay.length));
  }, [selectedDay]);

  // return arrayFromTime
}

/**
 * Hook Effect to useScrollSync
 *
 * @export
 * @returns
 */
export function useScrollSync(containerListRef, collapseExpandedState) {
  let isSyncingOneScroll = false;
  let isSyncingTwoScroll = false;
  let isSyncingTabScroll = false;
  const scrollOne = React.useRef(null);
  const scrollTwo = React.useRef(null);
  const scrollTab = React.useRef(null);

  const handleScrollTop = (refList, scrollTop) => {
    refList.forEach((ref) => {
      ref.current.scrollTop = scrollTop;
    });
  };

  const isCurrentRefs =
    scrollOne?.current && scrollTwo?.current && scrollTab?.current;

  React.useEffect(() => {
    if (!isCurrentRefs) return;

    const offsetHeight = containerListRef?.current?.offsetHeight || 0;
    scrollTab.current.lastChild.style.height = offsetHeight + 'px';

    if (collapseExpandedState.isCollapseExpanded) {
      const scrollTo = {
        top:
          ((scrollTab.current.scrollHeight -
            collapseExpandedState.accordionCollapseOffsetHeight) /
            collapseExpandedState.itemsLength) *
          collapseExpandedState.index,
        left: 0,
        behavior: 'smooth',
      };
      scrollOne.current.scrollTo(scrollTo);
      scrollTwo.current.scrollTo(scrollTo);
      scrollTab.current.scrollTo(scrollTo);
    }
  }, [scrollTab, collapseExpandedState, containerListRef]);

  function handleScrollOne() {
    if (!isSyncingOneScroll && isCurrentRefs) {
      isSyncingTwoScroll = true;
      isSyncingTabScroll = true;
      handleScrollTop([scrollTwo, scrollTab], scrollOne.current.scrollTop);
    }
    isSyncingOneScroll = false;
  }
  function handleScrollTwo() {
    if (!isSyncingTwoScroll && isCurrentRefs) {
      isSyncingOneScroll = true;
      isSyncingTabScroll = true;
      handleScrollTop([scrollOne, scrollTab], scrollTwo.current.scrollTop);
    }
    isSyncingTwoScroll = false;
  }

  function handleScrollTab() {
    if (!isSyncingTabScroll && isCurrentRefs) {
      isSyncingOneScroll = true;
      isSyncingTwoScroll = true;
      handleScrollTop([scrollOne, scrollTwo], scrollTab.current.scrollTop);
    }
    isSyncingTabScroll = false;
  }

  return [
    scrollOne,
    scrollTwo,
    scrollTab,
    handleScrollOne,
    handleScrollTwo,
    handleScrollTab,
  ];
}

/**
 * Hooks effect to useCurrentTime
 *
 * @export
 * @returns
 */
export function useCurrentTime(swiperVirtualSizePxWidth, dispatch) {
  const EPGCurrentTime = () => {
    const dateNow = Date.now();
    const { from, totalMilliseconds: totalMillisecondsNow } =
      informationOfTheDay(dateNow);
    const totalMillisecondsTime = differenceInMilliseconds(dateNow, from);

    dispatch(
      loadCurrentTime({
        date: dateNow,
        swiperPxWidthOnAllSlides:
          (swiperVirtualSizePxWidth / totalMillisecondsNow) *
          totalMillisecondsTime,
      }),
    );
  };

  const [intervalId, setIntervalId] = React.useState(false);
  React.useEffect(() => {
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(false);
    }
    EPGCurrentTime();
    const myIntervalR = setInterval(EPGCurrentTime, 15000);
    setIntervalId(myIntervalR);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(false);
      }
    };
  }, [swiperVirtualSizePxWidth]);
}

/**
 * Hooks effect to position up in change select day
 *
 * @export
 * @returns
 */
export function useScrollToUpInSelectDay(
  selectedDay,
  dispatch,
  scrollOne,
  scrollTwo,
) {
  React.useEffect(() => {
    const scrollTo = {
      top: 0,
      left: 0,
      behavior: 'smooth',
    };

    scrollOne.current.scrollTo(scrollTo);
    scrollTwo.current.scrollTo(scrollTo);
    dispatch(deselectedProgramItem());
  }, [selectedDay]);
}

export function usePagination(
  epgPage,
  content,
  loadMoreDataInComponent,
  setIsEpgLoading,
) {
  React.useEffect(() => {
    if (epgPage?.nextPage && content.length <= epgPage?.totalItems) {
      loadMoreDataInComponent();
    }

    if (epgPage?.currentPage === 1) {
      setIsEpgLoading(true);
    }
    if (
      epgPage?.nextPage === epgPage?.lastPage ||
      epgPage?.currentPage === epgPage?.lastPage
    ) {
      setIsEpgLoading(false);
    }
  }, [epgPage?.nextPage]);
}
